import { Capacitor } from "@capacitor/core";
import { v4 as uuidv4 } from "uuid";
import { captureMessage, SentryReact } from "~/config/sentry";
import { speechFailureDetails } from "./snag-speech-errors";

type SpeechDetails = Record<string, boolean | number | string | undefined>;
type SpeechEvent = SpeechDetails & { event: string; timestamp: number };
const history: SpeechEvent[] = [];
const reported = new Map<string, number>();

export function recordSpeechEvent(
  event: string,
  details: SpeechDetails = {},
  warning = false
) {
  // Observability must never break capture, preparation or cleanup.
  try {
    const data: SpeechEvent = {
      ...details,
      appBuild:
        typeof window === "undefined" ? undefined : window.appInfos?.build,
      appVersion:
        typeof window === "undefined" ? undefined : window.appInfos?.version,
      event,
      isOnline: typeof navigator === "undefined" ? undefined : navigator.onLine,
      platform: Capacitor.getPlatform(),
      timestamp: Date.now(),
    };
    history.push(data);
    if (history.length > 50) history.shift();
    SentryReact.addBreadcrumb({
      category: "visits.dictation",
      data,
      level: warning ? "warning" : "info",
      message: event,
    });
    if (!warning) return;
    const key = `${event}:${details.stage ?? ""}:${
      details.errorCode ?? details.reason ?? ""
    }`;
    const last = reported.get(key);
    // Deduplicate background failures, but retain every user-initiated failure.
    if (!details.runId && last !== undefined && Date.now() - last < 300000)
      return;
    if (reported.size >= 100) reported.clear();
    reported.set(key, Date.now());
    captureMessage(`Snag dictation: ${event}`, {
      extra: data,
      fingerprint: [
        "snag-dictation",
        event,
        String(details.stage ?? ""),
        String(details.errorCode ?? details.reason ?? ""),
      ],
      level: "warning",
      tags: {
        errorCode: details.errorCode,
        event,
        feature: "snag-dictation",
        reason: details.reason,
        stage: details.stage,
      },
    });
  } catch {
    /* Telemetry is best effort. */
  }
}

export const getSpeechDiagnosticsEvents = () => history.slice();

export function createSpeechRun(language: string) {
  const runId = uuidv4();
  const startedAt = Date.now();
  let finished = false;
  const event = (name: string, details: SpeechDetails = {}, warning = false) =>
    recordSpeechEvent(
      name,
      { ...details, elapsedMs: Date.now() - startedAt, language, runId },
      warning
    );
  const measure = async <T>(
    stage: string,
    action: () => Promise<T>,
    waitingAfterMs = 30000
  ): Promise<T> => {
    const start = Date.now();
    event("stage-started", { stage });
    // Report a stalled bridge without releasing the serialization lock while
    // native recognition may still be running.
    const waiting = setTimeout(() => {
      event("stage-waiting", { durationMs: Date.now() - start, stage }, true);
    }, waitingAfterMs);
    try {
      const result = await action();
      event("stage-succeeded", { durationMs: Date.now() - start, stage });
      return result;
    } catch (cause) {
      event(
        "stage-failed",
        {
          ...speechFailureDetails(cause),
          durationMs: Date.now() - start,
          stage,
        },
        true
      );
      throw cause;
    } finally {
      clearTimeout(waiting);
    }
  };
  const finish = (result: string) => {
    if (finished) return;
    finished = true;
    event("dictation-finished", { result });
  };
  event("dictation-started");
  return { event, finish, measure };
}

export type SpeechRun = ReturnType<typeof createSpeechRun>;
export type { SpeechDetails };

export { speechFailureDetails } from "./snag-speech-errors";
